import React, { useMemo } from "react";
import StatsCard from "../../common/New/StatsCard";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../context/SettingsContext";
import {
  ChunkLoadingIndicator,
  useProgressiveOverviewData,
} from "../../manageData/Analytics/useProgressiveOverviewData";

const COLORS = {
  blue: "#0B84F3",
  navy: "#142B55",
  red: "#F34D59",
  green: "#32C77F",
  amber: "#F8B94C",
  text: "#162238",
  muted: "#718096",
  grid: "#E9EDF3",
};
const WARNING_RATIO = 0.02;
const number = (value) => Number.parseFloat(value) || 0;
const formatSpeedValue = (value) => {
  const rounded = Math.round(Number(value) * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};
const parseDurationToMinutes = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "object") {
    if (value.minutes !== undefined && value.minutes !== null && value.minutes !== "") {
      const minutes = Number(value.minutes);
      if (Number.isFinite(minutes)) return minutes;
    }
    if (value.milliseconds !== undefined && value.milliseconds !== null) {
      const milliseconds = Number(value.milliseconds);
      if (Number.isFinite(milliseconds)) return milliseconds / 60000;
    }
    if (value.string) return parseDurationToMinutes(value.string);
    return null;
  }

  const text = String(value).trim().toLowerCase();
  if (!text || text === "n/a" || text === "na" || text.includes("ongoing")) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return null;

  const dayMatch = text.match(/(\d+(?:\.\d+)?)\s*d/);
  const hourMatch = text.match(/(\d+(?:\.\d+)?)\s*h/);
  const minuteMatch = text.match(/(\d+(?:\.\d+)?)\s*m(?!s)/);
  const secondMatch = text.match(/(\d+(?:\.\d+)?)\s*s/);
  if (dayMatch || hourMatch || minuteMatch || secondMatch) {
    return (
      (Number(dayMatch?.[1]) || 0) * 1440 +
      (Number(hourMatch?.[1]) || 0) * 60 +
      (Number(minuteMatch?.[1]) || 0) +
      (Number(secondMatch?.[1]) || 0) / 60
    );
  }

  const clockMatch = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (clockMatch) {
    return (
      (Number(clockMatch[1]) || 0) * 60 +
      (Number(clockMatch[2]) || 0) +
      (Number(clockMatch[3]) || 0) / 60
    );
  }

  const numeric = Number.parseFloat(text);
  return Number.isFinite(numeric) ? numeric : null;
};
const durationMinutes = (row) => {
  const candidates = [
    row?.durationMinutes,
    row?.overspeedDuration,
    row?.totalDuration,
    row?.duration,
    row?.durationInMinutes,
  ];
  for (const value of candidates) {
    const minutes = parseDurationToMinutes(value);
    if (minutes > 0) return minutes;
  }

  if (row?.durationSeconds != null || row?.durationInSeconds != null) {
    const seconds = number(row.durationSeconds || row.durationInSeconds);
    if (seconds > 0) return seconds / 60;
  }

  const start = eventDate(row, ["startTime", "fromTime", "start"]);
  const end = eventDate(row, ["endTime", "toTime", "end"]);
  if (start && end && end > start) return (end - start) / 60000;
  return 0;
};
const formatDuration = (minutes) => {
  if (!Number.isFinite(minutes) || minutes <= 0) return "0 mins";
  if (minutes < 1) return `${Math.max(1, Math.round(minutes * 60))} secs`;
  if (minutes < 60) return `${Math.round(minutes)} mins`;
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};
const eventDate = (row, keys) => {
  for (const key of keys) {
    const raw = row?.[key];
    if (!raw) continue;
    const text = String(raw).trim();
    if (/^\d{1,2}:\d{2}/.test(text)) {
      const anchor = new Date(row?.timestamp || row?.date || row?.createdAt || Date.now());
      if (Number.isNaN(anchor.getTime())) continue;
      const [hours, minutes, seconds] = text.split(":").map(Number);
      const date = new Date(anchor);
      date.setHours(hours || 0, minutes || 0, seconds || 0, 0);
      return date.getTime();
    }
    const date = new Date(raw);
    if (!Number.isNaN(date.getTime())) return date.getTime();
  }
  return null;
};
const eventHour = (row) => {
  const raw = row?.timestamp || row?.time || row?.startTime || row?.createdAt;
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && raw >= 0 && raw <= 23) return Math.floor(raw);
  const text = String(raw).trim();
  if (/^\d{1,2}:\d{2}/.test(text)) {
    const hour = Number.parseInt(text, 10);
    return hour >= 0 && hour <= 23 ? hour : null;
  }
  if (!/[T\s]\d{1,2}:\d{2}/.test(text)) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date.getHours();
};
const fillAverageSeries = (timeline, fallback) => {
  const known = timeline
    .map((point, index) => (point.sampleCount ? index : -1))
    .filter((index) => index >= 0 && index < 24);

  if (!known.length) {
    timeline.forEach((point) => {
      point.average = fallback > 0 ? +fallback.toFixed(1) : 0;
    });
    return;
  }

  for (let index = 0; index < known[0]; index += 1) {
    timeline[index].average = timeline[known[0]].average;
  }

  for (let cursor = 0; cursor < known.length - 1; cursor += 1) {
    const left = known[cursor];
    const right = known[cursor + 1];
    const start = timeline[left].average;
    const end = timeline[right].average;
    const span = right - left;
    for (let index = left + 1; index < right; index += 1) {
      if (!timeline[index].sampleCount) {
        timeline[index].average = +(start + ((end - start) * (index - left)) / span).toFixed(1);
      }
    }
  }

  const last = known[known.length - 1];
  for (let index = last + 1; index < timeline.length; index += 1) {
    timeline[index].average = timeline[last].average;
  }
};
const vehicleName = (row, index = 0) =>
  row?.vehicleName ||
  row?.deviceName ||
  row?.vehicleNumber ||
  `Vehicle-${index + 1}`;
const vehicleCode = (row) =>
  row?.vehicleNumber || row?.registrationNumber || row?.deviceId || "—";
const formatClock = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};
const speedColor = (speed, limit) => {
  if (!speed || !limit) return "#E7EBF0";
  if (speed >= limit) return "#F0525E";
  if (speed >= limit * (1 - WARNING_RATIO)) return "#F7D647";
  return "#65D897";
};

function Panel({ title, children, progress, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #DDE2EA",
        borderRadius: 1.5,
        overflow: "hidden",
        position: "relative",
        ...sx,
      }}
    >
      <ChunkLoadingIndicator progress={progress} />
      <Box sx={{ px: 2, py: 1.4, borderBottom: "1px solid #EEF1F5" }}>
        <Typography sx={{ fontSize: 13, color: COLORS.text, fontWeight: 700 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 1.5 }}>{children}</Box>
    </Paper>
  );
}

const ChartTooltip = ({ active, payload, label }) =>
  active && payload?.length ? (
    <Paper sx={{ px: 1.2, py: 0.8 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{label}</Typography>
      {payload.map((item) => (
        <Typography key={item.dataKey} sx={{ color: item.color, fontSize: 10 }}>
          {item.name}: {item.value}
        </Typography>
      ))}
    </Paper>
  ) : null;

export default function SpeedAnalytics({
  vehiclesData = [],
  selectedDateRange = "today",
  customDateRange = null,
}) {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const { chartDataStates, error, chunkProgress, isCancelling } =
    useProgressiveOverviewData(selectedDateRange, customDateRange);
  const overspeedState = chartDataStates.overspeed_vehicles;
  const activityState = chartDataStates.activity_vehicles;
  const incidents = useMemo(
    () => overspeedState?.data?.data || [],
    [overspeedState],
  );
  const activity = useMemo(
    () => activityState?.data?.data || [],
    [activityState],
  );
  const loading =
    !error &&
    (isCancelling ||
      overspeedState?.loading ||
      activityState?.loading ||
      (!overspeedState && !activityState));

  const analytics = useMemo(() => {
    const map = new Map();
    const ensure = (name, row = {}) => {
      if (!map.has(name))
        map.set(name, {
          name,
          code: vehicleCode(row),
          incidents: [],
          averageSpeed: 0,
          maxSpeed: 0,
          duration: 0,
        });
      return map.get(name);
    };
    vehiclesData.forEach((vehicle, index) =>
      ensure(vehicleName(vehicle, index), vehicle),
    );
    activity.forEach((row, index) => {
      const entry = ensure(vehicleName(row, index), row);
      entry.averageSpeed = Math.max(
        entry.averageSpeed,
        number(row.averageSpeed),
      );
    });
    let longestDuration = 0;
    incidents.forEach((row, index) => {
      const entry = ensure(vehicleName(row, index), row);
      entry.incidents.push(row);
      entry.maxSpeed = Math.max(
        entry.maxSpeed,
        number(row.speed || row.maxSpeed),
      );
      const minutes = durationMinutes(row);
      entry.duration += minutes;
      if (minutes > longestDuration) longestDuration = minutes;
      if (!entry.code || entry.code === "—") entry.code = vehicleCode(row);
    });
    const vehicles = [...map.values()]
      .filter(
        (item) => item.incidents.length || item.averageSpeed || item.maxSpeed,
      )
      .sort(
        (a, b) =>
          b.maxSpeed - a.maxSpeed || b.incidents.length - a.incidents.length,
      );
    const limit =
      number(settings?.maxSpeed) || number(overspeedState?.data?.maxSpeed) || 70;
    const maxRecorded = Math.max(0, ...vehicles.map((item) => item.maxSpeed));
    const average = vehicles.length
      ? vehicles.reduce((sum, item) => sum + item.averageSpeed, 0) /
        vehicles.length
      : 0;
    const activitySpeeds = activity
      .map((row) => number(row.averageSpeed || row.avgSpeed))
      .filter((speed) => speed > 0);
    const fleetAverage = activitySpeeds.length
      ? activitySpeeds.reduce((sum, speed) => sum + speed, 0) / activitySpeeds.length
      : average;
    const timeline = Array.from({ length: 25 }, (_, hour) => ({
      hour: hour === 24 ? "24:00" : `${String(hour).padStart(2, "0")}:00`,
      average: 0,
      overspeed: 0,
      sampleSum: 0,
      sampleCount: 0,
      spikeBase: 0,
      spike: 0,
    }));
    activity.forEach((row) => {
      const hour = eventHour(row);
      const speed = number(row.averageSpeed || row.avgSpeed);
      if (hour == null || speed <= 0 || speed >= limit) return;
      const point = timeline[hour];
      point.sampleSum += speed;
      point.sampleCount += 1;
    });
    incidents.forEach((row) => {
      const hour = eventHour(row);
      const speed = number(row.speed || row.maxSpeed);
      if (hour == null || speed <= 0) return;
      const point = timeline[hour];
      if (speed >= limit) {
        point.overspeed = Math.max(point.overspeed, speed);
        return;
      }
      point.sampleSum += speed;
      point.sampleCount += 1;
    });
    timeline.forEach((point) => {
      if (point.sampleCount) {
        point.average = +(point.sampleSum / point.sampleCount).toFixed(1);
      }
    });
    fillAverageSeries(timeline, fleetAverage);
    timeline.forEach((point) => {
      const baseline = point.average || 0;
      point.spikeBase = baseline;
      point.spike = point.overspeed
        ? +Math.max(point.overspeed - baseline, 0).toFixed(1)
        : 0;
    });
    return {
      vehicles: vehicles.slice(0, 9),
      limit,
      warningStart: limit * (1 - WARNING_RATIO),
      maxRecorded,
      average,
      longestDuration,
      timeline,
    };
  }, [activity, incidents, overspeedState?.data?.maxSpeed, settings?.maxSpeed, vehiclesData]);

  const topFive = analytics.vehicles.slice(0, 5);
  const incidentChartData = topFive.map((vehicle) => ({
    ...vehicle,
    incidentCount: vehicle.incidents.length,
  }));
  const heatHours = [
    "00",
    "02",
    "04",
    "06",
    "08",
    "10",
    "12",
    "14",
    "16",
    "18",
    "20",
    "22",
  ];
  const yMax = Math.max(
    10,
    Math.ceil(
      Math.max(
        analytics.limit,
        ...analytics.timeline.map((point) =>
          Math.max(point.average || 0, (point.spikeBase || 0) + (point.spike || 0)),
        ),
      ) / 10,
    ) * 10,
  );

  return (
    <Box sx={{ color: COLORS.text }}>
      {error && (
        <Typography color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(5, 1fr)",
          },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <StatsCard
          title={t("Overspeed Incidents")}
          value={String(incidents.length)}
          icon={SpeedOutlinedIcon}
          tone="red"
          loading={loading}
        />
        <StatsCard
          title={t("Highest Speed Recorded")}
          value={`${analytics.maxRecorded.toFixed(0)} km/h`}
          icon={TrendingUpOutlinedIcon}
          tone="green"
          trend={
            analytics.maxRecorded
              ? `↑ ${Math.max(0, analytics.maxRecorded - analytics.limit).toFixed(1)}%`
              : ""
          }
          trendDirection="up"
          loading={loading}
        />
        <StatsCard
          title={t("Longest Overspeed Duration")}
          value={formatDuration(analytics.longestDuration)}
          icon={TimerOutlinedIcon}
          tone="amber"
          loading={loading}
        />
        <StatsCard
          title={t("Average Speed")}
          value={`${analytics.average.toFixed(1)} km/h`}
          icon={TrendingUpOutlinedIcon}
          tone="blue"
          loading={loading}
        />
        <StatsCard
          title={t("Vehicles with Overspeed")}
          value={String(
            analytics.vehicles.filter((v) => v.incidents.length).length,
          )}
          icon={DirectionsCarOutlinedIcon}
          tone="red"
          loading={loading}
        />
      </Box>
      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <CircularProgress size={15} />
          <Typography sx={{ fontSize: 11, color: COLORS.muted }}>
            {t("Loading analytics...")}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Panel
          title={t("Overspeed Incidents")}
          progress={chunkProgress.overspeed_vehicles}
        >
          <ResponsiveContainer width="100%" height={215}>
            <BarChart
              data={incidentChartData}
              margin={{ top: 12, right: 8, left: -25, bottom: 0 }}
            >
              <CartesianGrid vertical={false} stroke={COLORS.grid} />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 9 }} />
              <Tooltip content={<ChartTooltip />} />
              <Bar
                dataKey="incidentCount"
                name={t("Incidents")}
                fill={COLORS.red}
                radius={[2, 2, 0, 0]}
                barSize={22}
              />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel
          title={t("Maximum Speed by Vehicle (km/h)")}
          progress={chunkProgress.overspeed_vehicles}
        >
          <ResponsiveContainer width="100%" height={215}>
            <BarChart
              data={topFive}
              margin={{ top: 12, right: 8, left: -18, bottom: 0 }}
            >
              <CartesianGrid vertical={false} stroke={COLORS.grid} />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} />
              <YAxis tick={{ fontSize: 9 }} />
              <Tooltip content={<ChartTooltip />} />
              <ReferenceLine
                y={analytics.limit}
                stroke={COLORS.red}
                strokeDasharray="3 3"
                label={{
                  value: t("Speed Limit"),
                  fontSize: 9,
                  fill: COLORS.red,
                }}
              />
              <Bar
                dataKey="maxSpeed"
                name={t("Speed")}
                fill={COLORS.navy}
                radius={[2, 2, 0, 0]}
                barSize={22}
              />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel
          title={t("Average Speed by Vehicle (km/h)")}
          progress={chunkProgress.activity_vehicles}
        >
          <ResponsiveContainer width="100%" height={215}>
            <BarChart
              data={topFive}
              margin={{ top: 12, right: 8, left: -18, bottom: 0 }}
            >
              <CartesianGrid vertical={false} stroke={COLORS.grid} />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} />
              <YAxis tick={{ fontSize: 9 }} />
              <Tooltip content={<ChartTooltip />} />
              <Bar
                dataKey="averageSpeed"
                name={t("Average Speed")}
                fill={COLORS.blue}
                radius={[2, 2, 0, 0]}
                barSize={22}
              />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(0, .9fr) minmax(0, 1.1fr)",
          },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Panel
          title={t("Speed Heatmap (Average Speed)")}
          progress={chunkProgress.overspeed_vehicles}
        >
          <Box sx={{ overflowX: "auto" }}>
            <Box
              sx={{
                minWidth: 500,
                display: "grid",
                gridTemplateColumns: "80px repeat(12, 1fr)",
                gap: "3px",
                alignItems: "center",
              }}
            >
              <Box />
              {heatHours.map((hour) => (
                <Typography
                  key={hour}
                  sx={{ fontSize: 8, color: COLORS.muted, textAlign: "center" }}
                >
                  {hour}
                </Typography>
              ))}
              {analytics.vehicles.slice(0, 9).flatMap((vehicle) => [
                <Typography
                  key={`${vehicle.name}-label`}
                  noWrap
                  sx={{ fontSize: 9, color: COLORS.muted }}
                >
                  {vehicle.name}
                </Typography>,
                ...heatHours.map((hour) => {
                  const matches = vehicle.incidents.filter((row) => {
                    const hourValue = eventHour(row);
                    return (
                      hourValue != null &&
                      Math.floor(hourValue / 2) * 2 === Number(hour)
                    );
                  });
                  const bucketSpeed = matches.length
                    ? matches.reduce(
                        (sum, row) => sum + number(row.speed || row.maxSpeed),
                        0,
                      ) / matches.length
                    : 0;
                  const speed = bucketSpeed;
                  const title = speed
                    ? `${vehicle.name} ${hour}:00 — ${speed.toFixed(1)} km/h`
                    : `${vehicle.name} ${hour}:00 — ${t("No data")}`;
                  return (
                    <Box
                      key={`${vehicle.name}-${hour}`}
                      title={title}
                      sx={{
                        height: 18,
                        bgcolor: speedColor(speed, analytics.limit),
                        borderRadius: "1px",
                      }}
                    />
                  );
                }),
              ])}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: 1.5,
                mt: 1.5,
              }}
            >
              {[
                [`< ${formatSpeedValue(analytics.warningStart)} km/h`, "#65D897"],
                [
                  `${formatSpeedValue(analytics.warningStart)}–${formatSpeedValue(analytics.limit)} km/h`,
                  "#F7D647",
                ],
                [`≥ ${formatSpeedValue(analytics.limit)} km/h`, "#F0525E"],
                [t("No data"), "#E7EBF0"],
              ].map(([label, color]) => (
                <Box
                  key={label}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <Box sx={{ width: 8, height: 8, bgcolor: color }} />
                  <Typography sx={{ fontSize: 8, color: COLORS.muted }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Panel>
        <Panel
          title={t("Top Speed Violators")}
          progress={chunkProgress.overspeed_vehicles}
        >
          <TableContainer sx={{ maxHeight: 285 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    "Rank",
                    "Vehicle",
                    "Max Speed (km/h)",
                    "Speed Limit (km/h)",
                    "Overspeed Count",
                    "Total Duration",
                    "Last Occurred",
                  ].map((heading) => (
                    <TableCell
                      key={heading}
                      sx={{
                        bgcolor: "#F5F7FA",
                        color: COLORS.muted,
                        fontSize: 8,
                        fontWeight: 700,
                        py: 1,
                      }}
                    >
                      {t(heading)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {topFive.map((vehicle, index) => (
                  <TableRow key={vehicle.name}>
                    <TableCell sx={{ fontSize: 9 }}>{index + 1}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: 9,
                          color: COLORS.text,
                          fontWeight: 700,
                        }}
                      >
                        {vehicle.name}
                      </Typography>
                      <Typography sx={{ fontSize: 8, color: COLORS.blue }}>
                        {vehicle.code}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 9 }}>
                      {vehicle.maxSpeed.toFixed(0)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 9 }}>
                      {analytics.limit}
                    </TableCell>
                    <TableCell sx={{ fontSize: 9 }}>
                      {vehicle.incidents.length}
                    </TableCell>
                    <TableCell sx={{ fontSize: 9 }}>
                      {formatDuration(vehicle.duration)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 9 }}>
                      {formatClock(
                        vehicle.incidents[vehicle.incidents.length - 1]
                          ?.timestamp ||
                          vehicle.incidents[vehicle.incidents.length - 1]?.time,
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!topFive.length && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{ color: COLORS.muted, py: 4 }}
                    >
                      {t("No speed data available")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Panel>
      </Box>
      <Panel
        title={t("Top Speed Violators")}
        progress={chunkProgress.overspeed_vehicles}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 0.5,
          }}
        >
          {[
            {
              key: "events",
              label: t("Overspeed Events"),
              mark: (
                <Box
                  sx={{ width: 10, height: 10, bgcolor: COLORS.red, borderRadius: "1px" }}
                />
              ),
            },
            {
              key: "average",
              label: t("Average Speed (km/h)"),
              mark: (
                <Box sx={{ width: 16, height: 2, bgcolor: COLORS.blue, borderRadius: 1 }} />
              ),
            },
            {
              key: "limit",
              label: `${t("Speed Limit")} (${formatSpeedValue(analytics.limit)} km/h)`,
              mark: (
                <Box
                  sx={{
                    width: 16,
                    borderTop: `2px dotted ${COLORS.red}`,
                  }}
                />
              ),
            },
          ].map((item) => (
            <Box
              key={item.key}
              sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
            >
              {item.mark}
              <Typography sx={{ fontSize: 11, color: COLORS.muted }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart
            data={analytics.timeline}
            margin={{ top: 12, right: 16, left: 4, bottom: 0 }}
          >
            <CartesianGrid vertical={false} stroke={COLORS.grid} />
            <XAxis
              dataKey="hour"
              interval={1}
              tick={{ fontSize: 9, fill: COLORS.muted }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, yMax]}
              tick={{ fontSize: 9, fill: COLORS.muted }}
              axisLine={false}
              tickLine={false}
              width={48}
              label={{
                value: t("Speed (km/h)"),
                angle: -90,
                position: "insideLeft",
                offset: 4,
                style: { fontSize: 10, fill: COLORS.muted, textAnchor: "middle" },
              }}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const row = payload[0]?.payload;
                if (!row) return null;
                return (
                  <Paper sx={{ px: 1.2, py: 0.8 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{label}</Typography>
                    {row.overspeed > 0 && (
                      <Typography sx={{ color: COLORS.red, fontSize: 10 }}>
                        {t("Overspeed Events")}: {Math.round(row.overspeed)} km/h
                      </Typography>
                    )}
                    <Typography sx={{ color: COLORS.blue, fontSize: 10 }}>
                      {t("Average Speed (km/h)")}: {Number(row.average || 0).toFixed(1)}
                    </Typography>
                    <Typography sx={{ color: COLORS.red, fontSize: 10 }}>
                      {t("Speed Limit")}: {formatSpeedValue(analytics.limit)} km/h
                    </Typography>
                  </Paper>
                );
              }}
            />
            <Area
              type="linear"
              dataKey="spikeBase"
              stackId="overspeed"
              stroke="none"
              fillOpacity={0}
              legendType="none"
              activeDot={false}
              isAnimationActive={false}
            />
            <Area
              type="linear"
              dataKey="spike"
              stackId="overspeed"
              name={t("Overspeed Events")}
              stroke="none"
              fill={COLORS.red}
              fillOpacity={0.55}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="average"
              name={t("Average Speed (km/h)")}
              stroke={COLORS.blue}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <ReferenceLine
              y={analytics.limit}
              stroke={COLORS.red}
              strokeDasharray="4 4"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Panel>
    </Box>
  );
}
